import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { ISaleMovement } from '../../shared/models/sale/sale-movement';
import { ISaleMovementDetail } from '../../shared/models/sale/sale-movement-detail';
import { User } from '../../shared/models/identity/user';
import { PaymentMethod } from '../../shared/models/sale/paymentMethod';
import { IMaterialFilterSet } from '../../shared/models/sale/material-filter-set';
import { IAdvancePaymentListItem } from '../../shared/models/reception/advance-payment-list-item';
import { MaterialCategory } from '../../shared/models/sale/material-category.enum';
import { CredipagoDetail } from '../../shared/models/sale/credipago-detail.model';
import { IDiscountPrice } from '../../shared/models/product/discount-price';
import { DiscountType, SaleDiscountType } from '../../shared/models/sale/discount-type.enum';

interface ISaleState {
  transactionProductsSelected: ISaleMovementDetail[];
  transactionSummary?: ISaleMovement;
  seller?: User;
  exchangeRate: number;
  hasUsd?: boolean;
  paymentMethods: PaymentMethod[];
  filterSet?: IMaterialFilterSet;
  isBudget?: boolean;
  advancePayments: IAdvancePaymentListItem[];
  crediPagos: CredipagoDetail[];
  outstandingBalance: number;
  isAdvancePaymentSale?: boolean;
  idemKey: string;
}

const toSerializableDate = (value?: Date | string | null): string | undefined => {
  if (value == null) return undefined;
  return value instanceof Date ? value.toISOString() : value;
};

const initialState: ISaleState = {
  transactionProductsSelected: [],
  transactionSummary: undefined,
  exchangeRate: 3.5,
  seller: undefined,
  paymentMethods: [],
  filterSet: {
    stock: 1,
    category: MaterialCategory.SparePart,
  },
  isBudget: false,
  advancePayments: [],
  crediPagos: [],
  outstandingBalance: 0,
  isAdvancePaymentSale: false,
  idemKey: '',
};

const saleSlice = createSlice({
  name: 'saleSlice',
  initialState,
  reducers: {
    setIdemKey: (state, action: PayloadAction<{ value: string }>) => {
      state.idemKey = action.payload.value;
    },
    setProductsSelected: (state, action: PayloadAction<{ value: ISaleMovementDetail[] }>) => {
      state.transactionProductsSelected = action.payload.value;
    },
    addProductToCart: (state, action: PayloadAction<{ product: ISaleMovementDetail }>) => {
      const newProduct = action.payload.product;

      // Verificar si el producto tiene promociones (nuevo sistema con array)
      const hasPromotions = newProduct.promotions && newProduct.promotions.length > 0;
      // Verificar si tiene promoci�n legacy (sistema antiguo)
      const hasLegacyPromotion = newProduct.promotionId;

      // Para productos con promociones (nuevo sistema o legacy)
      if (hasPromotions || hasLegacyPromotion) {
        // Obtener el primer promotionId para comparar (nuevo o legacy)
        const promotionId = hasPromotions
          ? newProduct.promotions![0].promotionId
          : newProduct.promotionId;
        const promotionIndex = hasPromotions
          ? newProduct.promotions![0].promotionIndex
          : newProduct.promotionIndex;

        const existingIndex = state.transactionProductsSelected.findIndex(
          (item) =>
            item.material.productId === newProduct.material.productId &&
            // Comparar por promotionId (legacy o nuevo)
            ((item.promotionId === promotionId && item.promotionIndex === promotionIndex) ||
              // O comparar por el primer elemento del array promotions
              (item.promotions &&
                item.promotions[0]?.promotionId === promotionId &&
                item.promotions[0]?.promotionIndex === promotionIndex))
        );

        if (existingIndex >= 0) {
          // Misma promoci�n ? Sumar cantidades
          const existingProduct = state.transactionProductsSelected[existingIndex];
          const newQuantity = (existingProduct.quantity || 0) + (newProduct.quantity || 0);
          state.transactionProductsSelected[existingIndex] = {
            ...existingProduct,
            quantity: newQuantity,
            subtotal: newQuantity * existingProduct.unitPrice,
            totalLine: newQuantity * (existingProduct.unitPrice - existingProduct.discountValue),
          };
        } else {
          // Promoci�n diferente o nueva ? Agregar como nuevo producto
          state.transactionProductsSelected.push(newProduct);
        }
      } else {
        // Productos normales (sin promoci�n) ? L�gica original
        const existingIndex = state.transactionProductsSelected.findIndex(
          (item) => item.material.productId === newProduct.material.productId
        );

        if (existingIndex >= 0) {
          const existingProduct = state.transactionProductsSelected[existingIndex];
          const newQuantity = (existingProduct.quantity || 0) + (newProduct.quantity || 0);
          state.transactionProductsSelected[existingIndex] = {
            ...existingProduct,
            quantity: newQuantity,
            subtotal: newQuantity * existingProduct.unitPrice,
            totalLine: newQuantity * (existingProduct.unitPrice - existingProduct.discountValue),
          };
        } else {
          state.transactionProductsSelected.push(newProduct);
        }
      }
    },
    setTransactionSummary: (state, action: PayloadAction<{ value?: ISaleMovement }>) => {
      const value = action.payload.value;

      state.transactionSummary = value
        ? {
            ...value,
            issueDate: toSerializableDate(value.issueDate),
            dueDate: toSerializableDate(value.dueDate),
            created: toSerializableDate(value.created),
          }
        : undefined;
    },
    setSeller: (state, action: PayloadAction<{ value?: User }>) => {
      state.seller = action.payload.value;
    },
    setPaymentMethods: (state, action: PayloadAction<{ value: PaymentMethod[] }>) => {
      state.paymentMethods = action.payload.value;
    },
    setToggleFreight(state, action: PayloadAction<{ id: string; checked: boolean }>) {
      const product = state.transactionProductsSelected.find(
        (p) => p.material.productId === action.payload.id
      );
      if (product) {
        product.freight = action.payload.checked;

        // Nuevo sistema: verificar por array promotions
        if (product.promotions && product.promotions.length > 0) {
          const firstPromo = product.promotions[0];
          if (firstPromo.isPrimary) {
            state.transactionProductsSelected.forEach((p) => {
              if (
                p.promotions &&
                p.promotions[0]?.promotionId === firstPromo.promotionId &&
                !p.promotions[0]?.isPrimary
              ) {
                p.freight = action.payload.checked;
              }
            });
          }
        }
        // Legacy: verificar por promotionId
        else if (product.promotionId && product.isPromotionPrimary) {
          state.transactionProductsSelected.forEach((p) => {
            if (p.promotionId === product.promotionId && !p.isPromotionPrimary) {
              p.freight = action.payload.checked;
            }
          });
        }
      }
    },
    setIsVehicleActivationRequiredToggle(
      state,
      action: PayloadAction<{ id: string; checked: boolean }>
    ) {
      const product = state.transactionProductsSelected.find(
        (p) => p.material.productId === action.payload.id
      );
      if (product) {
        product.isVehicleActivationRequired = action.payload.checked;
      }
    },
    setToggleFreightAll(state, action: PayloadAction<{ checked: boolean }>) {
      state.transactionProductsSelected.forEach((p) => {
        p.freight = action.payload.checked;
      });
    },
    setExchangeRate: (state, action: PayloadAction<{ value: number }>) => {
      state.exchangeRate = action.payload.value;
    },
    setOutstandingBalance: (state, action: PayloadAction<{ value: number }>) => {
      state.outstandingBalance = action.payload.value;
    },
    updateItemPrice(
      state,
      action: PayloadAction<{
        id: string;
        unitPrice: string;
        isDarkStore?: boolean;
        isDealer?: boolean;
        discountPrice?: IDiscountPrice;
      }>
    ) {
      const { id, isDarkStore, isDealer, discountPrice } = action.payload;
      let { unitPrice } = action.payload;
      const product = state.transactionProductsSelected.find((p) => p.material.productId === id);
      if (product) {
        let unitPriceValue = Number.parseFloat(unitPrice.replace(/[^0-9.-]+/g, '')) || 0;
        let minPrice = 1;
        let maxPrice = product?.material.unitPrice ?? 0;
        if (isDealer) {
          minPrice = 0.1;
          maxPrice = 999999999.99;
        }
        if (
          (unitPriceValue > maxPrice || unitPriceValue < minPrice) &&
          (isDarkStore || isDealer) &&
          product.material.category !== MaterialCategory.Service
        ) {
          unitPrice = product.material.unitPrice.toFixed(2);
          unitPriceValue = Number.parseFloat(unitPrice.replace(/[^0-9.-]+/g, '')) || 0;
        }
        let discountValue = 0;
        if (isDarkStore && product.material.category !== MaterialCategory.Service) {
          discountValue =
            product.unitPrice - Number.parseFloat(unitPrice.replace(/[^0-9.-]+/g, '')) || 0;
          unitPriceValue = product.material.unitPrice;
        } else if (
          isDealer &&
          product.material.category !== MaterialCategory.Service &&
          discountPrice?.discountValue
        ) {
          discountValue = discountPrice?.discountValue;
        } else if (product.material.category == MaterialCategory.Service) {
          discountValue = unitPriceValue * (product.discountPercentage / 100);
        }
        const quantity = product.quantity ?? 0;
        const subtotal = unitPriceValue * quantity;
        const discountSapValue = Math.min(subtotal, discountValue * quantity);
        let availableForCoupons = Math.max(0, subtotal - discountSapValue);

        const getCouponDiscount = (
          couponIndex: number,
          previousDiscount: number,
          previousUnitDiscount: number
        ) => {
          const couponLine = product.couponsLine?.[couponIndex];
          if (!couponLine?.enabled || !couponLine.coupon || availableForCoupons <= 0) return 0;

          const couponValue = Number(couponLine.coupon.discountValue) || 0;
          if (couponLine.coupon.discountType === DiscountType.Percentage) {
            return Math.min(availableForCoupons, (availableForCoupons * couponValue) / 100);
          }

          if (couponLine.coupon.discountType === DiscountType.Cash) {
            // El importe ya fue distribuido al aplicar el cupon. Conservamos esa asignacion
            // al editar el precio y solo la limitamos si el nuevo subtotal es menor.
            const assignedDiscount = Math.max(previousDiscount, previousUnitDiscount * quantity);
            return Math.min(availableForCoupons, assignedDiscount);
          }

          return 0;
        };

        const commercialCouponDiscount = getCouponDiscount(
          1,
          product.alternativeDiscountValue ?? 0,
          product.discountUnitCouponValue ?? 0
        );
        availableForCoupons = Math.max(0, availableForCoupons - commercialCouponDiscount);

        const employeeCouponDiscount = getCouponDiscount(
          2,
          product.alternativeDiscountValue2 ?? 0,
          product.discountUnitCouponValue2 ?? 0
        );
        const hasCoupon = commercialCouponDiscount > 0 || employeeCouponDiscount > 0;
        const hasSapDiscount = discountSapValue > 0;
        const totalDiscount = Math.min(
          subtotal,
          discountSapValue + commercialCouponDiscount + employeeCouponDiscount
        );

        product.discountValue = totalDiscount;
        product.unitPriceService = unitPrice;
        product.discountUnitValue = discountValue;
        product.discountUnitSapValue = discountValue;
        product.discountSapValue = discountSapValue;
        product.alternativeDiscountValue = commercialCouponDiscount;
        product.alternativeDiscountValue2 = employeeCouponDiscount;
        product.discountType = hasSapDiscount
          ? hasCoupon
            ? SaleDiscountType.Both
            : SaleDiscountType.Sap
          : hasCoupon
            ? SaleDiscountType.Coupon
            : SaleDiscountType.None;
        product.material.discountValue = discountValue;
        product.material.unitPrice = unitPriceValue;
        product.unitPrice = unitPriceValue;
        product.netUnitPrice = Math.max(0, unitPriceValue - discountValue);
        product.subtotal = subtotal;
        product.totalLine = Math.max(0, subtotal - totalDiscount);
      }
    },
    setFilterSet: (state, action: PayloadAction<{ value?: IMaterialFilterSet }>) => {
      state.filterSet = action.payload.value;
    },
    setIsBudget: (state, action: PayloadAction<{ value: boolean }>) => {
      state.isBudget = action.payload.value;
    },
    setInitialState: (state) => {
      state.transactionProductsSelected = initialState.transactionProductsSelected;
      state.transactionSummary = initialState.transactionSummary;
      state.exchangeRate = initialState.exchangeRate;
      state.filterSet = initialState.filterSet;
      state.isBudget = initialState.isBudget;
      state.paymentMethods = initialState.paymentMethods;
      state.crediPagos = initialState.crediPagos;
      state.isAdvancePaymentSale = initialState.isAdvancePaymentSale;
    },
    setCrediPagos: (state, action: PayloadAction<{ value: CredipagoDetail[] }>) => {
      state.crediPagos = action.payload.value;
    },
    setAdvancePayments: (state, action: PayloadAction<{ value: IAdvancePaymentListItem[] }>) => {
      state.advancePayments = action.payload.value;
    },
    setIsAdvancePaymentSale: (state, action: PayloadAction<{ value: boolean }>) => {
      state.isAdvancePaymentSale = action.payload.value;
    },
    addNormalPromotionToCart: (
      state,
      action: PayloadAction<{
        originalProductCode: string;
        promotionId: string;
        promotionType: string;
      }>
    ) => {
      const { originalProductCode, promotionId, promotionType } = action.payload;

      const productIndex = state.transactionProductsSelected.findIndex(
        (item) => item.material.code === originalProductCode
      );

      if (productIndex < 0) return;

      const product = state.transactionProductsSelected[productIndex];
      const currentPromotions = product.promotions || [];
      if (currentPromotions.length === 0) return;

      const alreadyExists = currentPromotions.some((p) => p.promotionId === promotionId);
      if (alreadyExists) return;

      const newPromotions = [
        ...currentPromotions,
        {
          promotionId,
          promotionIndex: 1,
          promotionType,
        },
      ];

      state.transactionProductsSelected[productIndex] = {
        ...product,
        promotions: newPromotions,
        promotionId: currentPromotions[0]?.promotionId,
        promotionType: product.promotionType ?? promotionType,
      };
    },
    // Agregar promoci�n cross a productos existentes (acumular normal + cross)
    addCrossPromotionToCart: (
      state,
      action: PayloadAction<{
        originalProductCode: string;
        crossPromotionId: string;
        crossPromotionType: string;
      }>
    ) => {
      const { originalProductCode, crossPromotionId, crossPromotionType } = action.payload;

      // Buscar el producto original en el carrito
      const productIndex = state.transactionProductsSelected.findIndex(
        (item) => item.material.code === originalProductCode
      );

      if (productIndex >= 0) {
        const product = state.transactionProductsSelected[productIndex];

        // Verificar si ya tiene promotions array
        const currentPromotions = product.promotions || [];

        // Requiere al menos 1 promoci�n normal aplicada antes de acumular cross
        if (currentPromotions.length === 0) return;

        // Evitar duplicar la misma cross promo
        const alreadyExists = currentPromotions.some((p) => p.promotionId === crossPromotionId);
        if (alreadyExists) return;

        const newPromotions = [
          ...currentPromotions,
          {
            promotionId: crossPromotionId,
            promotionIndex: 1,
          },
        ];

        // Actualizar el producto con ambas promociones
        state.transactionProductsSelected[productIndex] = {
          ...product,
          promotions: newPromotions,
          // Actualizar campos legacy para tracking interno
          promotionId: currentPromotions[0]?.promotionId, // Mantener la normal como primary
          crossPromotionId: crossPromotionId, // Tracking (�ltima cross aplicada)
          crossPromotionType: crossPromotionType,
        };
      } else {
        console.warn('?? [Redux] Product not found for code:', originalProductCode);
      }
    },

    applyCrossBonusDiscountToCart: (
      state,
      action: PayloadAction<{
        originalProductCode: string;
        discountType: number;
        discountValue: number;
      }>
    ) => {
      const { originalProductCode, discountType, discountValue } = action.payload;

      const productIndex = state.transactionProductsSelected.findIndex(
        (item) => item.material.code === originalProductCode
      );

      if (productIndex < 0) return;
      const product = state.transactionProductsSelected[productIndex];

      // Precio efectivo actual despu�s de descuentos previos (ej. combo)
      const effectiveBasePrice = Math.max(0, product.unitPrice - (product.discountUnitValue ?? 0));

      let discountUnitValue = 0;
      if (discountType === 1) {
        discountUnitValue = discountValue;
      } else {
        // Aplicar % sobre el precio efectivo actual (no el original)
        discountUnitValue = (effectiveBasePrice * discountValue) / 100;
      }

      discountUnitValue = Math.max(0, Math.min(effectiveBasePrice, discountUnitValue));
      const qty = product.quantity ?? 0;

      // Acumular descuento al existente (si hay)
      const existingDiscountUnitValue = product.discountUnitValue ?? 0;
      const totalDiscountUnitValue = existingDiscountUnitValue + discountUnitValue;

      product.discountUnitValue = totalDiscountUnitValue;
      product.discountUnitSapValue = totalDiscountUnitValue;
      product.discountSapValue = totalDiscountUnitValue * qty;
      product.discountValue = totalDiscountUnitValue * qty;
      product.discountPercentage = discountType === 1 ? 0 : discountValue;

      const netUnitPrice = Math.max(0, product.unitPrice - totalDiscountUnitValue);

      product.netUnitPrice = netUnitPrice;
      product.unitPriceService = product.unitPriceService ?? netUnitPrice.toFixed(2);
      product.subtotal = product.unitPrice * qty;
      product.totalLine = (product.unitPrice - totalDiscountUnitValue) * qty;
    },
  },
});

export const {
  setProductsSelected,
  addProductToCart,
  addNormalPromotionToCart,
  addCrossPromotionToCart,
  applyCrossBonusDiscountToCart,
  setTransactionSummary,
  setToggleFreight,
  setToggleFreightAll,
  setInitialState,
  setExchangeRate,
  updateItemPrice,
  setSeller,
  setPaymentMethods,
  setFilterSet,
  setIsBudget,
  setAdvancePayments,
  setCrediPagos,
  setIsVehicleActivationRequiredToggle,
  setOutstandingBalance,
  setIsAdvancePaymentSale,
  setIdemKey,
} = saleSlice.actions;
const SaleReducer = saleSlice.reducer;
export default SaleReducer;
